import React, { useEffect, useState } from 'react';
import {
  Tabs,
  App,
  Tooltip,
  Button,
  Drawer,
  Typography,
  Grid,
} from 'antd';

const { useBreakpoint } = Grid;
import { ArrowLeftOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import EmployeeList from './components/EmployeeList';
import EmployeeForm from './components/EmployeeForm';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEmployees, resetViewMode } from './employeesSlice';
import { getAccessType, Trial_Limits } from '../../lib/functions';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectAuthInfo } from '../Auth/authSelectors';


const { TabPane } = Tabs;
const { Title } = Typography;


export default function EmployeesPage() {
  const [active, setActive] = useState('list');
  const [access_type, access_type_set] = useState("");
  const [editingRecord, setEditingRecord] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const dispatch = useDispatch();
  const { modal } = App.useApp();
  const screens = useBreakpoint();

  const authInfo = useSelector(selectAuthInfo);

  const urlLocation = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(urlLocation.search);
  const shouldRestore = params.get("restoreDraft") === "true";
  const onboardId = params.get("onboardId");

  const { viewMode = false, } = useSelector((s) => s.employees || {});
  const { list: employees = [] } = useSelector((s) => s.employees || {});


  const handleEdit = (record) => { setEditingRecord(record); setActive('form'); };
  const handleCreate = () => {
    const trialMode = authInfo?.trial_mode;
    const userName = authInfo?.userInfo?.name || "User";
    if (trialMode == "yes" && employees.length >= Trial_Limits.employee_count) {
      modal.info({
        title: "Free Plan Limit Reached",
        content:
          `Hi ${userName}, you are currently in Free mode. Only 1 employee is allowed in Free Version.`,
      });
      return;
    }

    setEditingRecord(null);
    setActive('form');
    dispatch(resetViewMode());
  };

  useEffect(() => {
    const access = getAccessType("employee_entry");
    access_type_set(access);

    if (shouldRestore) setActive('form');

  }, []);

  useEffect(() => {
    if (onboardId) {
      setActive("form");
      setEditingRecord(null);

      // 🔥 CLEAN URL AFTER USING
    navigate("/hrpayroll/employees", { replace: true });
    }
  }, [onboardId]);


  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <Button
            type="text"
            icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
            onClick={() => navigate(-1)}
            data-testid="employees-back-button"
          />
          <Title
            level={2}
            style={{
              margin: 0,
              wordBreak: "break-word",
              fontSize: screens.md ? undefined : "22px",
            }}
          >
            Employees
          </Title>
        </div>

        <Tooltip title="Help">
          <Button
            type="text"
            icon={<QuestionCircleOutlined style={{ fontSize: 20 }} />}
            onClick={() => setHelpOpen(true)}
          />
        </Tooltip>
      </div>
      <Tabs activeKey={active} onChange={(k) => setActive(k)}>
        <TabPane tab="Employees List" key="list">
          <EmployeeList onEdit={handleEdit} onCreate={handleCreate} authInfo={authInfo} access_type={access_type} />
        </TabPane>
        {(access_type === "WRITE" || viewMode) && (
          <TabPane
            tab={
              editingRecord
                ? viewMode
                  ? "View Employee"
                  : "Edit Employee"
                : "New Employee"
            }
            key="form"
          >
            <EmployeeForm
              mode="full"
              visible={active === "form"}
              editing={editingRecord}
              onboardId={onboardId}
              authInfo={authInfo}
              access_type={access_type}

              onClose={() => {
                setActive("list");
                dispatch(fetchEmployees());
              }}
              setEditingRecord={setEditingRecord}
            />
          </TabPane>
        )}

      </Tabs>
      <Drawer
        title="Employee Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={430}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "12px"
            }}
          >
            Employee Management
          </h2>

          <p style={{ color: "#555", marginBottom: "16px",marginLeft:20 }}>
            The Employee Management module is used to manage employee records,
            onboarding details, payroll-related information, department assignments,
            and HR operations within the ERP HR & Payroll system.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View employee records and profiles</li>
              <li>Create new employee entries</li>
              <li>Edit employee information and assignments</li>
              <li>View employee profiles in read-only mode</li>
              <li>Restore onboarding drafts into employee forms</li>
              <li>Manage department and designation assignments</li>
              <li>Track employee onboarding conversions</li>
              <li>Switch between Employee List and Employee Form tabs</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Free mode allows limited employee creation.</li>
              <li>Employee records are linked with HR & Payroll operations.</li>
              <li>View mode disables editing functionality.</li>
              <li>Employee onboarding data can be restored into forms.</li>
              <li>Access permissions control create and edit actions.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </div>
  );
}